import { InlineCheckboxListControl, InlineOption, InlineOptionsListControl } from "@/Common/Components/Inputs/InlineOptionsControl";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { BaseOntarioObstetricalFileSubSection, BaseOntarioObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/Subcomponent/BaseOntarioObstetricalFileSubSection";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { VaccineState } from "@/Patient/Models/ObstetricalFile/Ontario/Postnatal/VaccineState";

class OntarioObstetricalFilePostnatalHistoryImmunizationAndConsumables extends BaseOntarioObstetricalFileSubSection<BaseOntarioObstetricalFileSubSectionProps, never> {
    public render(): JSX.Element {
        const postNatal = this.props.form.Postnatal;
        const fieldLimits = OntarioObstetricalFileFieldLimits.Page5;

        return (
            <div>
                <div className="row row-form">
                    <div className="col-md-6 flex-display flex-align-items-center">
                        <span className="bold margin-xl-right">{this.getString("SmokingLabel")}</span>
                        <div className="margin-md-right">
                            <InlineOptionsListControl.YesOrNo
                                id="postnatal-isSmoking"
                                value={postNatal.History.IsSmoking}
                                reverseOrder
                                abbreviate
                                onChange={value => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.IsSmoking = value?.Key)}
                                disabled={this.props.readonly}
                                locale={this.locale}
                            />
                        </div>
                        <div className="flex-display flex-align-items-center">
                            {this.renderSmallIntTextbox(
                                postNatal.History.NumberOfCigarettesPerDay,
                                value => this.props.mutateModelState(s => s.Postnatal.History.NumberOfCigarettesPerDay = value ? Number(value) : null),
                                "margin-sm-right"
                            )}
                            <span>{this.getString("ObstetricalFile_CigarettesPerDayAbbrevLabel")}</span>
                        </div>
                    </div>
                    <div className="col-md-6 flex-display flex-align-items-center flex-space-between">
                        <span className="bold margin-md-right">{this.getString("AlcoholLabel")}</span>
                        <span className="margin-md-right">
                            <InlineOptionsListControl.YesOrNo
                                id="postnatal-isDrinking"
                                value={postNatal.History.IsDrinking}
                                reverseOrder
                                abbreviate
                                onChange={value => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.IsDrinking = value?.Key)}
                                disabled={this.props.readonly}
                                locale={this.locale}
                            />
                        </span>
                        <div className="margin-md-right flex-display flex-align-items-center">
                            {this.renderSmallIntTextbox(
                                postNatal.History.NumberOfDrinkPerWeek,
                                value => this.props.mutateModelState(s => s.Postnatal.History.NumberOfDrinkPerWeek = value ? Number(value) : null),
                                "margin-sm-right"
                            )}
                            <span>{this.getString("ObstetricalFile_DrinksPerWeekLabel")}</span>
                        </div>
                        <div className="flex-display flex-align-items-center">
                            <span>{this.getString("ObstetricalFile_TACEScoreLabel")}</span>
                            {this.renderSmallIntTextbox(
                                postNatal.History.TaceScreeningScore,
                                value => this.props.mutateModelState(s => s.Postnatal.History.TaceScreeningScore = value ? Number(value) : null),
                                "margin-sm-left"
                            )}
                        </div>
                    </div>
                </div>
                <div className="row row-form">
                    <div className="col-md-12 flex-display flex-align-items-center">
                        <span className="bold margin-md-right">{this.getString("ObstetricalFile_NonPrescribedSubstancesAndDrugsWithExamplesLabel")}</span>
                        <InlineOptionsListControl.YesOrNo
                            id="postnatal-usesNonPrescribedSubstances"
                            value={postNatal.History.UsesNonPrescribedSubstances}
                            reverseOrder
                            abbreviate
                            onChange={value => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.UsesNonPrescribedSubstances = value?.Key)}
                            disabled={this.props.readonly}
                            locale={this.locale}
                        />
                    </div>
                </div>
                <div className="row row-form">
                    <div className="col-md-6">
                        {this.renderRadioButtonField(
                            "RubellaImmuneLabel",
                            <InlineCheckboxListControl
                                id="postnatal-history-rubellaImmune"
                                values={postNatal.History.RubellaImmunizationState}
                                onChange={values => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.RubellaImmunizationState = this.filterVaccineState(values, postNatal.History.RubellaImmunizationState))}
                                items={this.getImmunityStateOptions(true)}
                                disabled={this.props.readonly}
                                wrapOptions
                            />
                        )}
                    </div>
                    <div className="col-md-6 flex-display">
                        {this.renderRadioButtonField(
                            "InfluenzaLabel",
                            <InlineCheckboxListControl
                                id="postnatal-history-influenza"
                                values={postNatal.History.InfluenzaImmunizationState}
                                onChange={values => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.InfluenzaImmunizationState = values)}
                                items={this.getImmunityStateOptions(false)}
                                disabled={this.props.readonly}
                            />,
                            "margin-md-right"
                        )}
                        <div style={{ width: "115px" }}>
                            {this.renderSimpleField(
                                "",
                                this.controlRenderer.renderDateSelector({
                                    id: "postnatal-influenza-received-date",
                                    value: postNatal.History.InfluenzaImmunizationReceivedDate,
                                    onChange: date => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.InfluenzaImmunizationReceivedDate = date),
                                    className: "width-100"
                                })
                            )}
                        </div>
                    </div>
                </div>
                <div className="row row-form">
                    <div className="col-md-6">
                        {this.renderRadioButtonField(
                            "ObstetricalFile_PertussisUpToDateLabel",
                            <InlineCheckboxListControl
                                id="postnatal-history-pertussisImmunizationState"
                                values={postNatal.History.PertussisImmunizationState}
                                onChange={values => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.PertussisImmunizationState = this.filterVaccineState(values, postNatal.History.PertussisImmunizationState))}
                                items={this.getImmunityStateOptions(true)}
                                disabled={this.props.readonly}
                                wrapOptions
                            />
                        )}
                    </div>
                    <div className="col-md-6">
                        {this.renderSimpleField(
                            "OtherImmunizationsLabel",
                            this.controlRenderer.renderAutoCompleteTextbox({
                                value: postNatal.History.OtherImmunizations ?? "",
                                onChange: value => this.props.mutateModelState(s => s.Postnatal.History.OtherImmunizations = value),
                                autoCompleteItems: this.getOtherImmunizationsOptions(),
                                onAutoCompleteSelected: (code, _, value) => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.OtherImmunizations = value),
                                maxLength: fieldLimits.Postnatal_OtherImmunization.MaxChars
                            })
                        )}
                    </div>
                </div>
                <div className="row row-form">
                    <div className="col-md-12 flex-display">
                        <div style={{ width: "150px" }}>
                            {this.renderSimpleField(
                                "LastPapLabel",
                                this.controlRenderer.renderDateSelector({
                                    value: postNatal.History.LastPapTestDate,
                                    onChange: value => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.LastPapTestDate = value),
                                    id: "Exam_LastPapDate",
                                    className: "width-100"
                                }),
                                "margin-md-right"
                            )}
                        </div>
                        {this.renderSimpleField(
                            "ResultLabel",
                            this.controlRenderer.renderAutoCompleteTextbox({
                                value: postNatal.History.LastPaptestResult ?? "",
                                autoCompleteItems: this.getPapResultOptions(),
                                onChange: value => this.props.mutateModelState(s => s.Postnatal.History.LastPaptestResult = value),
                                onAutoCompleteSelected: (code, _, value) => this.props.mutateModelStateWithFormUpdate(s => s.Postnatal.History.LastPaptestResult = value),
                                maxLength: fieldLimits.Postnatal_LastPapTestResult.MaxChars,
                                disableMatchAutoSorting: true
                            }),
                            "flex-grow-1"
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderSmallIntTextbox(value: number, onChange: (value: string) => void, className: string): JSX.Element {
        return (
            <span className={`${className} align-right`} style={{ width: "60px" }}>
                {this.controlRenderer.renderTextbox({
                    value: value?.toString() ?? "",
                    onChange: onChange,
                    maxLength: 4,
                    isNumeric: true,
                    isInteger: true
                })}
            </span>
        );
    }

    private filterVaccineState(newValues: VaccineState[], oldValues: VaccineState[]): VaccineState[] {
        if (newValues.contains(VaccineState.IsImmune) && oldValues.contains(VaccineState.NotImmune)) {
            newValues = newValues.filter(p => p !== VaccineState.NotImmune);
        }
        else if (newValues.contains(VaccineState.NotImmune) && oldValues.contains(VaccineState.IsImmune)) {
            newValues = newValues.filter(p => p !== VaccineState.IsImmune);
        }

        return newValues;
    }

    private getImmunityStateOptions(includeYesOrNo: boolean = false): InlineOption<VaccineState>[] {
        return [
            ...(includeYesOrNo
                ? [
                    new InlineOption(VaccineState.IsImmune, this.getString("YesLabel")),
                    new InlineOption(VaccineState.NotImmune, this.getString("NoLabel"))
                ]
                : []
            ),
            new InlineOption(VaccineState.Discussed, this.getString("DiscussedLabel")),
            new InlineOption(VaccineState.Declined, this.getString("DeclinedLabel")),
            new InlineOption(VaccineState.Received, this.getString("ReceivedLabel"))
        ];
    }

    private getPapResultOptions(): SearchableItemModelT<string>[] {
        return [
            new SearchableItemModelT(this.getString("NormalLabel"), "PapResult_Normal"),
            new SearchableItemModelT(this.getString("AbnormalLabel"), "PapResult_Abnormal"),
            new SearchableItemModelT(this.getString("QuestionableLabel"), "PapResult_Questionable"),
            new SearchableItemModelT(this.getString("AscusAcronymLabel"), "PapResult_Ascus"),
            new SearchableItemModelT(this.getString("LsilAcronymLabel"), "PapResult_LSIL"),
            new SearchableItemModelT(this.getString("HsilAcronymLabel"), "PapResult_HSIL")
        ];
    }

    private getOtherImmunizationsOptions(): SearchableItemModelT<string>[] {
        return [
            new SearchableItemModelT(this.getString("Covid19Label"), "Covid19Label"),
            new SearchableItemModelT(this.getString("HepatitisBLabelAbbrevLabel"), "HepatitisBLabelAbbrevLabel")
        ];
    }
}

export { OntarioObstetricalFilePostnatalHistoryImmunizationAndConsumables };
